import { useState } from 'react';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { MatchTextArea } from 'foundations-components/transitional/components/MatchTextArea';
import { List } from 'foundations-components/transitional/components/List';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { contentIsTranslation, useContentObjectType } from 'content-management-data-lib';
import { CONTENT_ACTION_IDS } from '../constants/actionIds';
import useContentBulkUpdateAction from '../hooks/useContentBulkUpdateAction';
import { getContentId, getContentIdsWithMultiLangVariations, getContentNameWithLanguage, getHasMultilangVariations, getMultilangVariations } from '../utils/contentUtils';
import { AsyncActionTypes } from '../constants/actionTypes';
import { useContentActions } from '../providers/ContentActionsProvider';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MultiLangGroupList = ({
  crmObject
}) => /*#__PURE__*/_jsxs("span", {
  children: [getContentNameWithLanguage(crmObject), /*#__PURE__*/_jsx(List, {
    wrapChildren: true,
    styled: true,
    children: getMultilangVariations(crmObject).map(obj => /*#__PURE__*/_jsx("span", {
      children: getContentNameWithLanguage(obj)
    }, getContentId(obj)))
  })]
}, getContentId(crmObject));
const ObjectsList = ({
  isDeletingMultiLangGroup,
  crmObjects
}) => {
  if (isDeletingMultiLangGroup) {
    return /*#__PURE__*/_jsx(ScrollContainer, {
      maxHeight: 300,
      marginBlockStart: "500",
      children: /*#__PURE__*/_jsx(List, {
        wrapChildren: true,
        styled: true,
        children: /*#__PURE__*/_jsx(MultiLangGroupList, {
          crmObject: crmObjects[0]
        })
      })
    });
  }
  if (crmObjects.length > 1) {
    return /*#__PURE__*/_jsx(ScrollContainer, {
      maxHeight: 300,
      marginBlockStart: "500",
      children: /*#__PURE__*/_jsx(List, {
        wrapChildren: true,
        styled: true,
        children: crmObjects.map(obj => {
          if (getHasMultilangVariations(obj)) {
            /* eslint-disable-next-line react/jsx-key */
            return /*#__PURE__*/_jsx(MultiLangGroupList, {
              crmObject: obj
            });
          }
          return /*#__PURE__*/_jsx("span", {
            children: getContentNameWithLanguage(obj)
          }, getContentId(obj));
        })
      })
    });
  }
  return null;
};
const DeleteModal = ({
  crmObjects,
  onClose,
  isMultiStepModal,
  multiStepModalSuccessHandlers,
  renderBodyContent
}) => {
  const {
    onActionSuccess
  } = useContentActions();
  const [matched, setMatched] = useState(false);
  const {
    contentObjectTypeSingularNoun,
    contentObjectTypePluralNoun
  } = useContentObjectType();
  const contentName = crmObjects.length === 1 ? getContentNameWithLanguage(crmObjects[0]) : undefined;
  const count = crmObjects.length;
  const isDeletingMultiLangGroup = crmObjects.length === 1 && !contentIsTranslation(crmObjects[0]) && getHasMultilangVariations(crmObjects[0]);
  const handleMultiStepModalComplete = bulkUpdateSuccessData => {
    multiStepModalSuccessHandlers.forEach(handler => handler());
    onActionSuccess({
      actionId: CONTENT_ACTION_IDS.DELETE,
      data: bulkUpdateSuccessData
    });
    onClose();
  };
  const handleClose = () => {
    // Ensure to update objects from previous modal step updates if we cancel this one
    if (multiStepModalSuccessHandlers) {
      multiStepModalSuccessHandlers.forEach(handler => handler());
    }
    onClose();
  };
  const {
    handleBulkUpdateActionConfirm,
    requestInProgress
  } = useContentBulkUpdateAction({
    crmObjects: isDeletingMultiLangGroup ? [...crmObjects, ...getMultilangVariations(crmObjects[0])] : crmObjects,
    actionId: isDeletingMultiLangGroup ? CONTENT_ACTION_IDS.DELETE_LANGUAGE_GROUP : CONTENT_ACTION_IDS.DELETE,
    actionPath: 'archive',
    closeModal: onClose,
    asyncActionType: AsyncActionTypes.ALL,
    customActionSuccessHandler: isMultiStepModal && multiStepModalSuccessHandlers ? handleMultiStepModalComplete : undefined
  });
  return /*#__PURE__*/_jsxs(Modal, {
    use: "danger",
    "data-test-id": "delete-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: handleClose
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `content-management-actions-lib.modals.delete.header${isDeletingMultiLangGroup ? '.mlangGroup' : ''}`,
          options: {
            count,
            contentObjectTypeSingularNoun,
            contentObjectTypePluralNoun
          }
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [renderBodyContent ? renderBodyContent({
        isDeletingMultiLangGroup,
        count,
        contentName,
        contentObjectTypeSingularNoun,
        contentObjectTypePluralNoun
      }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `content-management-actions-lib.modals.delete.body${isDeletingMultiLangGroup ? '.mlangGroup' : ''}`,
        options: {
          count,
          contentName,
          contentObjectTypeSingularNoun,
          contentObjectTypePluralNoun
        }
      }), /*#__PURE__*/_jsx(ObjectsList, {
        isDeletingMultiLangGroup: isDeletingMultiLangGroup,
        crmObjects: crmObjects
      }), /*#__PURE__*/_jsx(FormLabel, {
        marginBlockStart: "600",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `content-management-actions-lib.modals.delete.deleteMatch.label${isDeletingMultiLangGroup ? '.mlangGroup' : ''}`,
          options: {
            count,
            contentObjectTypeSingularNoun,
            contentObjectTypePluralNoun
          }
        })
      }), /*#__PURE__*/_jsx(MatchTextArea, {
        "data-test-id": "match-text-input",
        match: I18n.text('content-management-actions-lib.modals.delete.deleteMatch.text', {
          count
        }),
        onMatchedChange: ({
          target: {
            value
          }
        }) => setMatched(value),
        size: "xxl"
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton
      // allows for treating this modal as a UIConfirmModal in Selenium
      , {
        "data-confirm-button": "accept",
        loading: requestInProgress,
        disabled: !matched,
        onClick: () => handleBulkUpdateActionConfirm({
          actionPayload: isDeletingMultiLangGroup ? {
            objectIds: getContentIdsWithMultiLangVariations(crmObjects)
          } : {}
        }),
        use: "danger",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "content-management-actions-lib.modals.delete.buttons.delete"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: handleClose,
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "content-management-actions-lib.modals.delete.buttons.cancel"
        })
      })]
    })]
  });
};
export default DeleteModal;